'use client'

import { createContext, useMemo, useRef } from 'react'

import { INDEXED_DB_NAME, INDEXED_DB_TABLE_NAME } from '../constants/constants'
import type { ChatbotMessage } from '../types/messageTypes'

interface ChatIndexedDbContext {
	openDatabase: (callback: () => void) => void
	getData: (callback: (data: ChatbotMessagesDb) => void) => void
	saveData: (data: ChatbotMessage[]) => void
	clearData: () => void
}

export const ChatbotIndexedDbContext = createContext<
	ChatIndexedDbContext | undefined
>(undefined)

interface ChatbotIndexedDbProviderProps {
	readonly children: React.ReactNode
}

type ChatbotMessagesDb = ChatbotMessage[] & { id: number }

const getDbObjectStore = (db: IDBDatabase) =>
	db
		.transaction([INDEXED_DB_TABLE_NAME], 'readwrite')
		.objectStore(INDEXED_DB_TABLE_NAME)

export function ChatbotIndexedDbProvider({
	children,
}: ChatbotIndexedDbProviderProps): React.ReactNode {
	const db = useRef<IDBDatabase | null>(null)

	const openDatabase = (callback: () => void): void => {
		const openRequest: IDBOpenDBRequest = indexedDB.open(INDEXED_DB_NAME, 1)

		openRequest.onsuccess = (event: Event) => {
			db.current = (event.target as IDBOpenDBRequest).result
			callback()
		}

		openRequest.onupgradeneeded = (event: IDBVersionChangeEvent) => {
			const result = (event.target as IDBOpenDBRequest).result
			if (!result.objectStoreNames.contains(INDEXED_DB_TABLE_NAME)) {
				result.createObjectStore(INDEXED_DB_TABLE_NAME, {
					keyPath: 'id',
					autoIncrement: true,
				})
			}
		}
	}

	const getData = (callback: (data: ChatbotMessagesDb) => void) => {
		if (!db.current) {
			return
		}

		const request = getDbObjectStore(db.current).getAll()
		request.onsuccess = () => {
			callback(request.result[0] as ChatbotMessagesDb)
		}
	}

	const saveData = (data: ChatbotMessage[]): void => {
		if (!db.current) {
			return
		}

		const request = getDbObjectStore(db.current).get(1)

		request.onsuccess = () => {
			const eventToSave = data
			;(eventToSave as ChatbotMessagesDb).id = 1

			if (!db.current) {
				return
			}

			getDbObjectStore(db.current).put(eventToSave)
		}
	}

	const clearData = (): void => {
		if (!db.current) {
			return
		}
		getDbObjectStore(db.current).clear()
	}

	const value = useMemo(
		() => ({
			openDatabase,
			getData,
			saveData,
			clearData,
		}),
		[openDatabase, getData, saveData, clearData]
	)

	return (
		<ChatbotIndexedDbContext.Provider value={value}>
			{children}
		</ChatbotIndexedDbContext.Provider>
	)
}
